import {z} from 'zod';

const osText = z.string().min(1).max(512).regex(/^[^\u0000-\u001f\u007f]*$/);

/** Read-only account environment view. Versions come only from the running guest. */
export const AccountVirtualMachineStatusSchema = z.object({
  accountId: z.uuid(),
  backend: z.enum(['vm', 'container']),
  state: z.enum(['unallocated', 'running', 'stopped', 'starting', 'stopping', 'paused', 'crashed', 'unavailable']),
  maintenance: z.enum(['ready', 'updating', 'update-failed', 'rolling-back', 'rollback-failed', 'os-changing']).nullable(),
  imageId: z.string().regex(/^sha256:[a-f0-9]{64}$/).nullable(),
  operatingSystem: z.object({
    id: z.string().min(1).max(512).regex(/^[a-z0-9._-]+$/),
    name: osText,
    versionId: osText.nullable(),
    kernelRelease: osText
  }).strict().nullable(),
  observedAt: z.iso.datetime(),
  guestStatus: z.enum(['live', 'unavailable', 'not-running', 'not-applicable']),
  recovery: z.object({
    available: z.boolean(),
    state: z.enum(['idle', 'running', 'failed']),
    error: z.string().min(1).max(4096).nullable()
  }).strict()
}).strict().superRefine((value, context) => {
  if ((value.operatingSystem !== null) !== (value.guestStatus === 'live')) {
    context.addIssue({code: 'custom', message: 'Linux version information requires a live guest observation.'});
  }
  if (value.backend === 'container' && (value.guestStatus !== 'not-applicable' || value.imageId !== null || value.maintenance !== null)) {
    context.addIssue({code: 'custom', message: 'An account awaiting VM migration cannot report VM metadata.'});
  }
  if (value.backend === 'vm' && value.guestStatus === 'not-applicable') {
    context.addIssue({code: 'custom', message: 'A VM must report whether its guest status is available.'});
  }
});

export type AccountVirtualMachineStatus = z.infer<typeof AccountVirtualMachineStatusSchema>;
